import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { createUserFeedback } from './data';
import { setUserData } from '../../../../../lib/redux/actions.jsx';
import { useNotification } from '../../../../../shared/Notification/useNotification';

/**
 * Submits a campaign rating once. On success the campaign is added to the
 * user's rated list in Redux so the rating modal is not offered again during
 * the same session.
 *
 * @param {{ campaignUuid: string }} options
 */
export const useCreateUserFeedbackMutation = ({ campaignUuid }) => {
  const dispatch = useDispatch();
  const campaignFeedbacksIds = useSelector(
    state => state.userData.campaignFeedbacksIds,
  );
  const { showError, showSuccess } = useNotification();
  const { t } = useTranslation(['shared', 'campaigns']);
  const inFlight = useRef(false);

  const { mutate, isLoading: isCreating } = useMutation(createUserFeedback, {
    onSuccess: () => {
      dispatch(
        setUserData({
          campaignFeedbacksIds: [...(campaignFeedbacksIds || []), campaignUuid],
        }),
      );
      Swal.close();
      showSuccess({ content: t('campaigns:feedback.notifySuccess') });
    },
    onError: () => {
      showError({ content: t('shared:errorText') });
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // The ref guards against a double click landing before React re-renders with isLoading.
  const createUserFeedbackMutation = feedback => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutate(feedback);
  };

  return { createUserFeedbackMutation, isCreating };
};
